<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import { estServeur } from '$plateforme/environnement';
  import { clic } from '../directives/actions.svelte';
  import ControleSegmente from '../navigation/ControleSegmente.svelte';
  import { creeLeFragmentDeNavigation, type FragmentDeNavigation } from '../navigation/fragmentDeNavigation.svelte';
  import { profilStore } from '../stores/profil.store';
  import ChampRecherche from '../ui/ChampRecherche.svelte';
  import { fabriqueFilAriane, type PropriétésFilAriane } from '../ui/filAriane';
  import FiltresBureau from '../ui/FiltresBureau.svelte';
  import FiltresMobile from '../ui/FiltresMobile.svelte';
  import Heros from '../ui/Heros.svelte';
  import IllustrationPasDeResultatDeRecherche from '../ui/IllustrationPasDeResultatDeRecherche.svelte';
  import CarteItem from './CarteItem.svelte';
  import {
    DroitAcces,
    Source,
    Typologie,
    type BesoinCyber,
    type ItemCyber,
    type RepartitionParBesoin,
  } from './Catalogue.types';
  import FiltreAccessibilite from './FiltreAccessibilite.svelte';
  import FiltreBesoin from './FiltreBesoin.svelte';
  import FiltreSource from './FiltreSource.svelte';
  import FiltreTypologie from './FiltreTypologie.svelte';
  import { CollectionGuide, Langue, type Guide } from './Guide.types';
  import FiltreCollection from './guides/FiltreCollection.svelte';
  import FiltreLangue from './guides/FiltreLangue.svelte';
  import { guidePourCarteItem } from './guides/guide';
  import InciteASAbonner from './guides/InciteASAbonner.svelte';
  import { catalogueStore } from './stores/catalogue.store';
  import { catalogueFiltre } from './stores/catalogueFiltre.store';
  import { chargeGuidesDansLeStore, guidesStore } from './stores/guides/guides.store';
  import { guidesFiltres } from './stores/guides/guidesFiltres.store';
  import { rechercheParCollection } from './stores/guides/rechercheParCollection.store';
  import { rechercheParLangue } from './stores/guides/rechercheParLangue.store';
  import { rechercheParBesoin } from './stores/rechercheParBesoin.store';
  import { rechercheParDroitAcces } from './stores/rechercheParDroitAcces.store';
  import { rechercheParSource } from './stores/rechercheParSource.store';
  import { rechercheParTypologie } from './stores/rechercheParTypologie.store';
  import { recherches } from './stores/recherches.store';
  import { rechercheTextuelle } from './stores/rechercheTextuelle.store';

  type Props = {
    itemsCyber?: ItemCyber[];
    repartition?: RepartitionParBesoin;
    guides?: Guide[];
  };

  let { itemsCyber, repartition, guides }: Props = $props();
  const itemsCyberInitiaux = untrack(() => itemsCyber);
  const guidesInitiaux = untrack(() => guides);
  const repartitionInitiale = untrack(() => repartition);

  if (itemsCyberInitiaux) {
    catalogueStore.initialise(itemsCyberInitiaux, repartitionInitiale);
  }
  if (guidesInitiaux) {
    guidesStore.initialise(guidesInitiaux.map(guidePourCarteItem));
  }

  const idsCollectionsGuide: Record<CollectionGuide, string> = {
    [CollectionGuide.LES_ESSENTIELS]: 'essentiels',
    [CollectionGuide.LES_FONDAMENTAUX]: 'fondamentaux',
    [CollectionGuide.IA]: 'ia',
    [CollectionGuide.CRYPTOGRAPHIE]: 'cryptographie',
    [CollectionGuide.SYSTEMES_INDUSTRIELS]: 'systemes-industriels',
    [CollectionGuide.CRISE_CYBER]: 'crise-cyber',
    [CollectionGuide.GESTION_DES_RISQUES_CYBER]: 'gestion-risques-cyber',
    [CollectionGuide.SUPERVISION_DE_SECURITE]: 'supervision-securite',
    [CollectionGuide.REMEDIATION]: 'remediation',
    [CollectionGuide.AUTRE]: 'autre',
  };
  const nomsCollectionsGuide = Object.fromEntries(
    Object.entries(idsCollectionsGuide).map(([cle, valeur]) => [valeur, cle as CollectionGuide])
  );
  const versIdsCollection = (collections: CollectionGuide[]): string[] =>
    collections.map((c) => idsCollectionsGuide[c]);

  const depuisIdsCollection = (idsCollection: string[]): CollectionGuide[] =>
    idsCollection.map((i) => nomsCollectionsGuide[i]);

  // Gestion du fragment
  let fragmentDeNavigation = creeLeFragmentDeNavigation();

  // Gestion de la section
  let idÉlémentSélectionné = $derived(
    fragmentDeNavigation.section === 'ressourcesEtServices' ? 'ressources-et-services' : 'guides'
  );

  // Gestion des filtres
  const reinitialiseFiltres = () => recherches.reinitialise();
  const appliqueLesFiltres = (fragmentDeNavigation: FragmentDeNavigation) => {
    $rechercheParBesoin = fragmentDeNavigation.extraisValeur<BesoinCyber>('besoin', null);
    $rechercheParLangue = fragmentDeNavigation.extraisTableau<Langue>('langues');
    $rechercheParCollection = depuisIdsCollection(fragmentDeNavigation.extraisTableau<CollectionGuide>('collections'));
    $rechercheTextuelle = fragmentDeNavigation.extraisValeur('q', '') ?? '';
    $rechercheParDroitAcces = fragmentDeNavigation.extraisTableau<DroitAcces>('accessibilite');
    $rechercheParTypologie = fragmentDeNavigation.extraisTableau<Typologie>('types');
    $rechercheParSource = fragmentDeNavigation.extraisTableau<Source>('sources');
  };
  $effect(() => appliqueLesFiltres(fragmentDeNavigation));
  $effect(() => {
    fragmentDeNavigation.change('besoin', $rechercheParBesoin);
    fragmentDeNavigation.change('langues', $rechercheParLangue);
    fragmentDeNavigation.change('collections', versIdsCollection($rechercheParCollection));
    fragmentDeNavigation.change('q', $rechercheTextuelle);
    fragmentDeNavigation.change('accessibilite', $rechercheParDroitAcces);
    fragmentDeNavigation.change('types', $rechercheParTypologie);
    fragmentDeNavigation.change('sources', $rechercheParSource);
    fragmentDeNavigation.actualise();
  });

  // Gestion du chargement
  let chargement = $state(false);
  onMount(async () => {
    try {
      chargement = true;
      await chargeGuidesDansLeStore();
    } finally {
      chargement = false;
    }
  });

  const élémentsDuCatalogue = $derived.by(() => {
    if (estServeur) {
      return [...$guidesFiltres.resultats, ...$catalogueFiltre.resultats];
    }
    return idÉlémentSélectionné === 'guides' ? $guidesFiltres.resultats : $catalogueFiltre.resultats;
  });
  const propriétésFilAriane: PropriétésFilAriane = { feuille: 'Guides et ressources', fondSombre: true };
</script>

<Heros
  cacheFilAriane={!!$profilStore}
  description="Trouvez les services et les ressources adaptés à vos besoins."
  format="banniere"
  segmentsFilAriane={fabriqueFilAriane(propriétésFilAriane, !!$profilStore)}
  theme="clair"
  titre="Les services et ressources cyber"
/>

<div class="barre-filtre-besoin">
  <div class="contenu-section">
    <FiltreBesoin />
  </div>
</div>

<FiltresMobile filtreActif={$recherches.filtreActif}>
  <FiltreBesoin />
  {#if idÉlémentSélectionné === 'guides'}
    <FiltreLangue />
    <FiltreCollection />
  {:else}
    <FiltreAccessibilite />
    <FiltreTypologie />
    <FiltreSource />
  {/if}

  <dsfr-button centered label="Réinitialiser les filtres" use:clic={reinitialiseFiltres}></dsfr-button>
</FiltresMobile>

<dsfr-container class="barre-recherche-mobile">
  <ChampRecherche bind:recherche={$rechercheTextuelle} />
</dsfr-container>

<div class="conteneur-controle-segmente">
  <ControleSegmente
    elements={[
      { id: 'guides', titre: 'Guides de l’ANSSI', icone: 'book-2-line', ancre: 'guides' },
      {
        id: 'ressources-et-services',
        titre: 'Services et outils',
        icone: 'list-check',
        ancre: 'ressourcesEtServices',
      },
    ]}
    bind:idÉlémentSélectionné
    {fragmentDeNavigation}
  ></ControleSegmente>
</div>

<dsfr-container class="contenu-catalogue">
  {#if !$profilStore && idÉlémentSélectionné === 'guides'}
    <div class="entete">
      <InciteASAbonner />
    </div>
  {/if}

  <div class="grille">
    <FiltresBureau filtreActif={$recherches.filtreActif}>
      {#snippet avantEntete()}
        <ChampRecherche bind:recherche={$rechercheTextuelle} />
      {/snippet}
      {#if idÉlémentSélectionné === 'guides'}
        <FiltreLangue />
        <FiltreCollection />
      {:else}
        <FiltreAccessibilite />
        <FiltreTypologie />
        <FiltreSource />
      {/if}
      <dsfr-button centered label="Réinitialiser les filtres" use:clic={reinitialiseFiltres}></dsfr-button>
    </FiltresBureau>

    {#each élémentsDuCatalogue as élément (élément.id)}
      <CarteItem item={élément} avecBoutonFavori />
    {:else}
      <div class="aucun-resultat">
        <div class="illustration">
          <IllustrationPasDeResultatDeRecherche />
        </div>
        {#if chargement}
          <p class="fr-h3">Chargement...</p>
        {:else}
          <p class="fr-h3">Désolé, aucun résultat trouvé</p>
          <dsfr-button label="Réinitialiser les filtres" use:clic={reinitialiseFiltres}></dsfr-button>
        {/if}
      </div>
    {/each}
  </div>
</dsfr-container>

<style lang="scss">
  .contenu-section {
    flex-direction: column;
  }

  .entete {
    padding-bottom: 0.5rem;
  }

  .conteneur-controle-segmente {
    padding-block: 1rem;
    background: var(--background-default-grey);
    position: sticky;
    top: 0;
    z-index: calc(var(--ground) + 751);

    display: grid;
    place-items: center;
  }
  .illustration {
    width: 175px;
    height: 317px;
  }
</style>
